<template>
    <div class="india-elects">
        <div class="zone-title zone-title--bg banded landing__zone-title--short">
            <div class="india-elects-logo"></div>
            <div class="gradient">
                <div class="container-title container-headline container-headline--no-image">
                    <h1>A Guide to India’s 2024 Elections<span>By Milan Vaishnav and Caroline Mallory</span></h1>
                </div>
            </div>
        </div>
        <div class="section background white intro">
            <div class="section foreground">
                <div class="introText cols">
                    <div class="col col-50">
                        <div class="gutters">
                            <h3>Indians will vote in general elections in April–May 2024, for the first time in five
                                years.</h3>
                            <p>The 2024 election will be the largest democratic exercise in
                                history. India has the most expensive elections in the world, surpassing even the United
                                States. In 2019, parties and candidates spent an estimated $8.7 billion to woo more than
                                900 million eligible voters.</p>
                            <p>In 2019, 8,054 candidates representing 673 parties stood for
                                elections for a shot at becoming a Member of Parliament (MP).</p>
                            <p>Nearly 615 million people—67.4 percent of Indians—voted in
                                2019: this was the highest voter turnout on record. For the first time in history, the
                                persistent gender gap between male and female turnout disappeared.</p>
                            <p>As of January 1, 2023, there were over 945 million registered
                                voters in India.</p>
                        </div>
                    </div>
                    <div class="col col-50 intro-graphics"></div>
                </div>
            </div>
        </div>
        <div class="section background lok-sabha">
            <div class="foreground">
                <div class="cols">
                    <div class="col col-50">
                        <div class="gutters">
                            <h2> <span>What’s at stake?</span></h2>
                            <p class="highlight">The 2024 general elections will decide who gets to
                                sit in India’s lower house of parliament, the Lok Sabha or House of the People.</p>
                            <p>It is the more powerful of the two houses that make up India’s
                                parliament:</p>
                            <ul class="clean-list">
                                <li class="gov">
                                    <div class="gutters">It can hold the government accountable by introducing and
                                        passing no confidence motions.</div>
                                </li>
                                <li class="money">
                                    <div class="gutters">It is in charge of the purse strings: all tax, borrowing, and
                                        spending bills only require the approval of the lower house.</div>
                                </li>
                                <li class="negotiations">
                                    <div class="gutters">It has more seats than the upper house (543, compared to a
                                        maximum of 250 for the latter), giving it greater heft in legislative
                                        negotiations.
                                    </div>
                                </li>
                            </ul>
                            <p>The party or coalition that attains a Lok Sabha majority will
                                nominate one of their elected members to serve as prime minister. The prime minister
                                will be entrusted with selecting ministers to serve in the cabinet. In India’s
                                parliamentary system, there are no term limits.</p>
                        </div>
                    </div>
                    <div class="col col-50"></div>
                </div>
            </div>
        </div>
        <div class="section background logistics">
            <div class="foreground">
                <div class="cols">
                    <div class="col col-50">
                        <div class="gutters">
                            <h2> <span>Mind-boggling logistics</span></h2>
                            <p class="highlight">India’s electoral rules say there must be a polling
                                station within 2 kilometers (1.2 miles) of every habitation.</p>
                            <p>This means many of India’s 11 million election workers must
                                trek across glaciers, deserts, jungles, and an ocean to make sure every eligible Indian
                                can vote.</p>
                            <p>For instance, the sole inhabitant of the remote Gir National
                                Park in Gujarat, home to the last free-ranging Asiatic lions, has his own polling
                                station complete with his own electronic voting machine, because there are no paper
                                ballots for in-person voting. </p>
                            <p>Nearly every vote is cast electronically, via 1.74 million
                                electronic voting machines across more than 1 million polling stations (as of 2019).
                            </p>
                        </div>
                    </div>
                    <div class="col col-50 logistics-graphic"></div>
                </div>
            </div>
            <div class="images logistics-bridge">
                <div class="caption">
                    <div></div>An election official carries a voting machine across a makeshift bridge in Jammu and
                    Kashmir.<br><br>(Photo: STRDEL/AFP via Getty Images)
                </div>
            </div>
        </div>
        <div class="section background timeline">
            <div class="foreground">
                <div class="cols">
                    <div class="col col-50">
                        <div class="gutters">
                            <h2> <span>Timeline</span></h2>
                            <p class="highlight">Making sure that over 900 million people can vote
                                securely is difficult to do in a single day, so the general elections take place over
                                several weeks. </p>
                            <p><strong>There is no single date when everyone votes.</strong>
                                Instead, the country votes in sequential phases by region. In 2019, elections transpired
                                over seven phases between April 11 and May 19, with all the votes counted on May 23. The
                                first phase of the election will take place in a specified set of geographic regions
                                with subsequent phases gradually moving across the country to cover other regions. </p>
                            <p><strong>There are no primary elections.</strong> Party leaders
                                have complete control over the nomination of candidates. If candidates fail to win party
                                backing, they can run as independents but are unlikely to do well. Of the 543 MPs
                                elected in 2019, only 4 were independent candidates. </p>
                            <p><strong>The number of candidates contesting elections in each
                                    electoral constituency varies widely.</strong> In 2019, the Nizamabad constituency
                                of Telangana saw 185 contestants throw their hat in the ring, while only 3 candidates
                                were in the fray in the Tura constituency in Meghalaya.</p>
                            <p><strong>Voters can officially cast “protest” votes.</strong>
                                Since 2013, voters have been able to select a “none of the above” (NOTA) option if they
                                want to exercise the right to vote but do not wish to support any of the candidates on
                                offer. In 2019, NOTA earned just over 1 percent of the total vote. However, NOTA is not
                                considered an effective protest vote because even if NOTA gets the most votes, it does
                                not trigger a repoll. Rather, the individual candidate with the second-most votes (after
                                NOTA) would be declared the winner.</p>
                            <p>This year’s campaign is expected to run over many weeks in
                                April–May 2024, with the exact electoral calendar to be announced in March. </p>
                        </div>
                    </div>
                    <div class="col col-50">
                        <div class="gutters">
                            <div class="varies-widely"></div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="section timeline-section">
                <ol>
                    <li class="entry odd">
                        <div class="text"><span>Week 1</span>
                            <p>The Election Commission of India announces election dates.
                                This triggers the Model Code of Conduct, a set of norms designed to discourage hate
                                speech, vote-buying, and preelection spending.</p>
                        </div>
                    </li>
                    <li class="entry even">
                        <div class="text"><span>Week 2</span>
                            <p>The Election Commission of India formally notifies the
                                elections.</p>
                        </div>
                    </li>
                    <li class="entry odd">
                        <div class="text"><span>Week 3</span>
                            <p>Candidates contesting the first phase of elections submit
                                their nomination papers to the Election Commission.</p>
                        </div>
                    </li>
                    <li class="entry even">
                        <div class="text"><span>Week 4</span>
                            <p>Official campaigning kicks off for the first phase.</p>
                        </div>
                    </li>
                    <li class="empty"></li>
                    <li class="entry odd">
                        <div class="text"><span>Week 6</span>
                            <p>Polling starts for the first phase. The process repeats on a
                                staggered basis for the subsequent phases until the entire country has been covered.
                            </p>
                        </div>
                    </li>
                    <li class="empty"></li>
                    <li class="empty"></li>
                    <li class="empty"></li>
                    <li class="entry even last">
                        <div class="text"><span>Week 10</span>
                            <p>Votes counted. The votes for all phases are announced and the
                                results are usually known within hours.</p>
                        </div>
                    </li>
                    <li class="empty"></li>
                </ol>
            </div>
        </div>
        <div class="section background purple players">
            <div class="foreground">
                <div class="cols">
                    <div class="col col-50">
                        <div class="gutters">
                            <h2> <span>The Players</span></h2>
                            <div class="big-players"></div>
                        </div>
                    </div>
                    <div class="col col-50">
                        <div class="gutters">
                            <p class="highlight bump-down">The incumbent</p>
                            <p>The ruling Bharatiya Janata Party (BJP) led by Prime Minister
                                Narendra Modi won 303 seats in 2019. Overall, the BJP-led National Democratic Alliance
                                (NDA) won a total of 352 seats, giving it a commanding majority. Preelection surveys
                                suggest that, if elections were held today, the NDA would once more win a clear majority
                                of seats in the Lok Sabha.
                            </p>
                            <p class="highlight bump-down">The principal challenger</p>
                            <p>The Indian National Congress (INC, also known as the Congress
                                Party), headed by Mallikarjun Kharge, is the BJP’s only pan-Indian rival. The Congress
                                Party has suffered two consecutive disastrous election performances, winning just 44
                                seats in 2014 and 52 seats in 2019. </p>
                        </div>
                    </div>
                </div>
                <p class="highlight">The question mark</p>
                <p>In July 2023, more than two dozen opposition parties announced their
                    intention to form an electoral alliance, the Indian National Developmental Inclusive Alliance
                    (INDIA), to wrest power from the NDA. INDIA, of which the Congress Party is the largest constituent
                    member, includes a wide diversity of parties, from the All India Trinamool Congress (the ruling
                    party in the state of West Bengal) and the Dravida Munnetra Kazhagam (the ruling party in Tamil
                    Nadu) to smaller parties like the Communist Party of India (Marxist–Leninist) Liberation and the
                    Indian Union Muslim League. In the run-up to the election, INDIA has been besieged by infighting and
                    defections.</p>
                <div class="cols other-parties">
                    <div class="col col-33 kazhagam">
                        <div class="gutters">
                            <p>Dravida Munnetra Kazhagam </p>
                            <p>Leader: M. K. Stalin</p>
                            <p>Base: Tamil Nadu</p>
                            <p>Seats (2019): 24</p>
                        </div>
                    </div>
                    <div class="col col-33 banerjee">
                        <div class="gutters">
                            <p>All India Trinamool Congress</p>
                            <p>Leader: Mamata Banerjee</p>
                            <p>Base: West Bengal</p>
                            <p>Seats (2019): 22</p>
                        </div>
                    </div>
                    <div class="col col-33 kejriwal">
                        <div class="gutters">
                            <p>Aam Aadmi Party</p>
                            <p>Leader: Arvind Kejriwal</p>
                            <p>Base: Delhi, Punjab</p>
                            <p>Seats (2019): 1</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="section background map">
            <div class="foreground">
                <div class="cols">
                    <div class="col col-50">
                        <div class="gutters">
                            <h2> <span>Electoral Map</span></h2>
                            <p class="highlight">Seats are divided among states in proportion to
                                their population: more people mean more seats.</p>
                            <p>Roughly 25 percent of seats are constitutionally reserved for
                                members hailing from one of two disadvantaged communities: Scheduled Castes (SC), also
                                known as Dalits, and Scheduled Tribes (ST), India’s tribal peoples or Adivasis.
                                Eighty-four seats are reserved for SCs and forty-seven seats are reserved for STs. In
                                these constituencies, only candidates from the protected groups can contest elections,
                                though all eligible adults can cast their votes. </p>
                            <p>Although India’s parliament recently passed a new measure to
                                reserve one-third of legislative seats for women, the implementation of this act has
                                been delayed until after 2024. </p>
                            <p>The candidate who secures the greatest number of votes in each
                                electoral constituency wins that seat. Candidates do not need to earn a majority of the
                                vote to be declared the victor.</p>
                        </div>
                    </div>
                    <div class="col col-50">
                        <div class="gutters">
                            <div class="disadvantaged"></div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="images voter">
                <div class="caption">
                    <div></div>A voter casts her ballot at a polling station in Uttar Pradesh.<br><br>(Photo: Money
                    Sharma / AFP via Getty Images)
                </div>
            </div>
        </div>
        <div class="section background battleground">
            <div class="foreground">
                <div class="cols">
                    <div class="col col-50">
                        <div class="gutters">
                            <h2> <span>Battleground states </span></h2>
                            <p class="highlight">There are many battleground states this year, but
                                three deserve special mention.</p>
                            <div class="accordion-trigger active"> Uttar Pradesh <span>80 seats</span></div>
                            <div class="accordion-content cols active">
                                <div class="col col-50">
                                    <div class="gutters">
                                        <p>Home to an estimated 230 million Indians, Uttar
                                            Pradesh is the single biggest electoral prize. In 2019, the BJP and a
                                            smaller coalition ally swept up 64 of the state’s seats, while two powerful
                                            regional parties—the Samajwadi Party and Bahujan Samaj Party—could only
                                            muster a disappointing 15 seats. </p>
                                    </div>
                                </div>
                                <div class="col col-50 uttar-pradesh"></div>
                            </div>
                            <div class="accordion-trigger"> Maharashtra <span>48 seats</span></div>
                            <div class="accordion-content cols">
                                <div class="col col-50">
                                    <div class="gutters">
                                        <p>In 2019, the BJP and its ally Shiv Sena won 23 and
                                            18 seats, respectively. Since then, Shiv Sena has split: one faction allied
                                            with the BJP to form the state government, and another—led by former state
                                            chief minister Uddhav Thackeray—joined hands with the Congress Party and the
                                            Nationalist Congress Party. </p>
                                    </div>
                                </div>
                                <div class="col col-50 maharashtra"></div>
                            </div>
                            <div class="accordion-trigger"> Karnataka<span>28 seats</span></div>
                            <div class="accordion-content cols">
                                <div class="col col-50">
                                    <div class="gutters">
                                        <p>The BJP nearly swept the southern state in 2019,
                                            winning 25 of the state’s seats (with a BJP-backed independent candidate
                                            winning a twenty-sixth seat). Replicating this feat will be an uphill battle
                                            as the Congress Party wrested control of the state from the BJP in 2023
                                            regional polls. However, in September 2023, an influential regional front
                                            known as the Janata Dal (Secular), which has often played the role of
                                            kingmaker, officially joined the BJP-led NDA. </p>
                                    </div>
                                </div>
                                <div class="col col-50 karnataka"></div>
                            </div>
                        </div>
                    </div>
                    <div class="col col-50">
                        <div class="gutters">
                            <div class="big-map"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="section background results">
            <div class="foreground">
                <div class="cols">
                    <div class="col col-50">
                        <div class="gutters">
                            <h2> <span>After the results</span></h2>
                            <p class="highlight">The president, whose election occurs on a separate
                                timeline, will ask the leading party to form a government.</p>
                            <p>If no single party wins an absolute majority, leading parties
                                will try to form a coalition by teaming up with smaller parties. </p>
                            <p>While some alliances are established before the election, many
                                alliances are negotiated after the results are announced and can even shift during a
                                government’s term.
                            </p>
                        </div>
                    </div>
                    <div class="col col-50 results-2019"></div>
                </div>
            </div>
        </div>
        <div class="section background white conclusion">
            <div class="foreground">
                <div class="doc-related">
                    <h3>Related Analysis from Carnegie</h3>
                    <ul class="clean-list">
                        <li class="preview-module doc-related__item preview-module--has-image"><a
                                href="https://carnegieendowment.org/podcasts/grand-tamasha?lang=en"
                                style="background-image: url(https://ceipimg.s3.amazonaws.com/static/GrandTamasha.gif);">
                                <div class="doc-related__item-text-container">
                                    <div class="title">Grand Tamasha</div>
                                    <div class="small-text">Milan Vaishnav</div>
                                </div>
                            </a></li>
                        <li class="preview-module doc-related__item preview-module--has-image"><a
                                href="https://carnegieendowment.org/projects/india-elects-2024"
                                style="background-image: url(https://ceipimg.s3.amazonaws.com/static/IndiaElects-socialshare.jpg);">
                                <div class="doc-related__item-text-container">
                                    <div class="title">India Elects 2024</div>
                                    <div class="small-text">Milan Vaishnav</div>
                                </div>
                            </a></li>
                        <li class="preview-module doc-related__item preview-module--has-image"><a
                                href="https://carnegieendowment.org/research/2023/12/decoding-indias-2024-election-contest?lang=en"
                                style="background-image:url(https://images.carnegieendowment.org/images/article_images/GettyImages-India-Electionjpg.jpg);">
                                <div class="doc-related__item-text-container">
                                    <div class="title">Decoding India’s 2024 Election Contest</div>
                                    <div class="small-text">Milan Vaishnav</div>
                                </div>
                            </a></li>
                        <li class="preview-module doc-related__item preview-module--has-image"><a
                                href="https://carnegieendowment.org/research/2023/12/how-ideology-shapes-indian-politics?lang=en"
                                style="background-image:url(https://images.carnegieendowment.org/images/article_images/GettyImages-bjp.jpg);">
                                <div class="doc-related__item-text-container">
                                    <div class="title">How Ideology Shapes Indian Politics</div>
                                    <div class="small-text">Nicholas Haas, Rajeshwari Majumdar</div>
                                </div>
                            </a></li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import FeatureFragment from '@fragments/pages/FeatureFragment.ts';

const pageData = await usePageData(FeatureFragment, 'features');

onMounted(() => {
    // I need to catch any time an existing accordion or overlay is clicked, so it looks for all clicks on anything and acts if the className includes sortable ....
    document.onclick = function (e) {
        // console.log(e.target)
        if (e.target.className && e.target.className.includes('accordion-trigger')) {
            if (e.target.className.includes('active')) {
                e.target.classList.toggle('active');
                e.target.nextElementSibling.classList.toggle('active');
            } else {
                e.target.classList.toggle('active');
                e.target.nextElementSibling.classList.toggle('active');
            }
        }
    };
});
</script>

<style lang="scss" scoped>
$gutter: 30px;
$site-width: 1000px;

$gold: #a28556;
$black: #000;
$white: #fff;
$teal: #00a79d;
$light-gray: #f5f5f5;
$yellow: #fbb040;
$primary-dark-blue: #072a42;
$gold-medium: #998b7b;
$gray-dark: #635a4f;

$roman-normal-book: 'Apercu-Medium', Arial, sans-serif;
$roman-italic-book: 'Apercu-Medium-Italic', Arial, sans-serif;
$roman-normal-bold: 'Apercu-Bold', 'Arial Black', sans-serif;
$roman-italic-bold: 'Apercu-Bold-Italic', 'Arial Black', sans-serif;

%bold-sans-text {
    font-family: $roman-normal-bold;
    font-weight: 700;
}

%italic-sans-text {
    font-family: $roman-normal-book;
    font-style: italic;
}

//layout basics
.foreground {
    position: relative;
    margin: 0 auto;
    box-sizing: content-box;
    max-width: $site-width;
    padding-left: calc($gutter / 2);
    padding-right: calc($gutter / 2);
}

.section {
    display: block;
    clear: both;
    margin-top: $gutter;
    margin-bottom: $gutter;
}

.cols {
    clear: both;
    //use a .no-gutters class to undo these default styles
    margin-left: calc($gutter / 2) * -1;
    margin-right: calc($gutter / 2) * -1;

    &:before,
    &:after {
        content: ' ';
        display: table;
        height: 0;
        clear: both;
        font-size: 1px;
        line-height: 0px;
        visibility: hidden;
    }
}

.cols--equal-heights {
    flex-wrap: wrap;

    &,
    >.col {
        display: flex;

        // @include bp(mobile) {
        //     display: block;
        // }
    }

    >.col {
        flex-direction: column;
    }

    .banded {
        flex: 1 0 auto;
    }
}

.cols {
    clear: both;
}

.col {
    float: left;

    .gutters {
        padding-left: calc($gutter / 2);
        padding-right: calc($gutter / 2);
    }

    // @include bp(mobile) {
    //     float: none;
    //     width: 100% !important;

    //     > .gutters {
    //         margin-left: 0;
    //         margin-right: 0;
    //     }
    // }
}

.col-80 {
    width: 80%;
}

.col-70 {
    width: 70%;
}

//two across
.col-50 {
    width: 50%;
}

.col-33 {
    width: 33%;
}

.col-30 {
    width: 30%;
}

//four across
.col-25 {
    width: 25%;
}

//five across
.col-20 {
    width: 20%;
}

.padded {
    padding: $gutter;
}

.gutter-right {
    margin-right: 30px;
}

.gutter-left {
    margin-left: 30px;
}

:deep(.accordion-content) {
    display: none;

    &.active {
        display: block;
        margin-top: 3rem;
    }
}

.india-elects {
    --paragraph-bottom-margin: 1.8rem;
    font-size: 1.3em;

    p {
        margin-bottom: var(--paragraph-bottom-margin);
    }
}

//india-elects colors
$primary-yellow: #efa43b;
$primary-purple: #6e2358;
$dark-purple: #432b6b;

.section {
    font-family: $roman-normal-book;
    margin-bottom: 0;
    margin-top: 0;
    z-index: 3;
}

.zone-title {
    background-color: $black;
    background-image: url(https://ceipimg.s3.amazonaws.com/static/hdr-india-elects-2024-GettyImages-1229375863.jpg);
    background-position: 3% center;
    background-repeat: no-repeat;
    background-size: cover;
    text-align: left;

    .gradient {
        background: -moz-linear-gradient(bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.89) 100%);
        background: -webkit-gradient(linear, left bottom, left bottom, color-stop(0%, rgba(0, 0, 0, 0.65)), color-stop(100%, rgba(0, 0, 0, 0)));
        background: -webkit-linear-gradient(top, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.89) 100%);
        background: -o-linear-gradient(bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.89) 100%);
        background: -ms-linear-gradient(bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.89) 100%);
        background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.89) 100%);
        filter: progid:DXImageTransform.Microsoft.gradient(startColorstr='#a6000000', endColorstr='#00000000', GradientType=0);
        box-sizing: content-box;
        width: 100%;
    }

    .container-title {
        padding-top: 480px;
        margin: 0 0 0 4%;
        width: 55%;
    }

    h1 {
        color: $white;
        display: inline-block;
        font-family: $roman-normal-bold;
        font-size: 4rem;
        margin: $gutter 0;
        padding: calc($gutter / 5) 20px 12px 20px;

        span {
            display: block;
            font-size: 1.5rem;
            margin-top: 1rem;
        }
    }

    .india-elects-logo {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/IndiaElects-Sticker.png);
        background-repeat: no-repeat;
        background-size: contain;
        color: transparent;
        height: 150px;
        float: right;
        margin: 0;
        width: 150px;
        bottom: -80px;
        right: 40px;
        position: relative;
        display: block;
        cursor: default;
    }
}

.background {
    padding: calc($gutter * 2) 0;
    position: relative;

    h2 {
        color: $white;
        margin-bottom: calc($gutter / 2);
        margin-left: calc($gutter * -2);

        span {
            background-color: $teal;
            display: inline-block;
            font-family: $roman-normal-bold;
            padding: calc($gutter / 4) calc($gutter * 2.5) calc($gutter / 4) calc($gutter * 2);
        }
    }

    &.white {
        background-color: $white;
    }

    &.purple {
        background-color: $primary-purple;
        color: $white;
    }

    &.teal {
        background-color: $teal;
        border-bottom: $primary-yellow 5px solid;
        border-top: none;
    }

    p.highlight {
        font-family: $roman-normal-bold;
        font-size: 2.2rem;
    }
}

.intro {
    border-top: none;
    display: block;
    position: relative;
    z-index: 3;

    .foreground {
        margin-top: calc($gutter * 2.5);
        margin-bottom: $gutter;
    }

    h3 {
        color: $black;
        font-family: $roman-normal-bold;
        margin-bottom: 1rem;
    }

    .col-50 .gutters {
        padding-right: 15px;
    }


    .intro-graphics {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/IndiaElects_Vital-Stats.png?v=1);
        background-repeat: no-repeat;
        background-size: contain;
        height: 400px;
        margin-top: 7%;
    }
}

.caption {
    background-color: $primary-yellow;
    bottom: 5%;
    color: $primary-purple;
    font-family: $roman-italic-bold;
    font-size: 1em;
    left: $gutter;
    padding: 20px;
    position: absolute;
    max-width: 450px;

    div {
        border-top: $primary-purple 10px solid;
        margin: 0 0 10px 0;
        max-width: 35%;
    }
}

.background-photo {
    background-position: center bottom;
    background-repeat: no-repeat;
    background-size: cover;
    -moz-box-shadow: 0px -3px 3px rgba(0, 0, 0, 0.3);
    -webkit-box-shadow: 0px -3px 3px rgba(0, 0, 0, 0.3);
    box-shadow: 0px -3px 3px rgba(0, 0, 0, 0.3);
    height: 100vh;
    width: 100%;

    &.women-voting {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/WomenVotingCards.jpg)
    }

    ;

    &.crowd {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/Crowds.jpg)
    }

    ;

    &.person-voting {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/PersonVoting.jpg)
    }

    ;

    &.poster {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/ElectionPoster.jpg)
    }

    ;
}

.lok-sabha {
    background-image: url(https://ceipimg.s3.amazonaws.com/static/parliament_duo.jpg);
    background-size: cover;
    color: $white;

    h2 span {
        background-color: $primary-purple;
    }

    ul {
        padding-bottom: calc($gutter * 2);
    }

    .extra-padded {
        padding-top: calc($gutter * 2.5);
    }

    .gov {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/IndiaElects_Icon1.png);
        background-position: top left;
        background-repeat: no-repeat;
        background-size: auto 100%;
        height: 57px;
    }

    .money {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/IndiaElects_Icon2.png);
        background-position: top left;
        background-repeat: no-repeat;
        background-size: auto 100%;
        height: 57px;
    }

    .negotiations {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/IndiaElects_Icon3.png);
        background-position: top left;
        background-repeat: no-repeat;
        background-size: auto 100%;
        height: 57px;
    }

    li .gutters {
        margin-top: 40px;
        margin-left: 70px;
    }
}

.logistics {
    height: auto;
    padding-bottom: 0;

    h2 span {
        background-color: $dark-purple;
    }

    .images {
        margin-top: calc($gutter * 2);
    }

    .logistics-graphic {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/IndiaElects_Distance.png);
        background-repeat: no-repeat;
        background-size: contain;
        background-position: bottom center;
        height: 500px;
    }

    .logistics-bridge {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/Logistics-Bridge.jpg);
        background-repeat: no-repeat;
        background-size: 100% auto;
        height: 950px;
    }
}

.timeline {
    padding-bottom: 0;

    h2 span {
        background-color: $dark-purple;
    }

    .col-30 .gutters {
        margin-left: calc($gutter * 2.5)
    }

    p strong {
        font-family: $roman-normal-bold;
    }

    .varies-widely {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/IndiaElects_CandidateDots.png);
        background-repeat: no-repeat;
        background-size: contain;
        height: 700px;
        background-position: top right;
    }

    .timeline-section {
        background-color: $light-gray;
        white-space: nowrap;
        overflow-x: hidden;

        ol {
            font-size: 0;
            width: 100vw;
            padding: 450px 0 300px 0;
            transition: all 1s;

            li {
                position: relative;
                display: inline-block;
                list-style-type: none;
                width: 9%;
                height: 30px;
                background: $primary-yellow;

                &:first-child {
                    margin-left: -1%;
                }

                &.entry:not(:last-child)::after {
                    content: '';
                    position: absolute;
                    top: 50%;
                    left: calc(100% + 1px);
                    bottom: 0;
                    width: 12px;
                    height: 12px;
                    transform: translateY(-50%);
                    border-radius: 50%;
                    background: $primary-purple;
                    z-index: 2;
                }

                div {
                    border-left: 3px dashed $primary-yellow;
                    position: absolute;
                    left: calc(100% + 7px);
                    width: 150%;
                    padding: 0 15px 15px 15px;
                    font-size: 1.8rem;
                    white-space: normal;

                    span {
                        color: $primary-purple;
                        font-family: $roman-normal-bold;
                        text-transform: uppercase;
                    }

                    p {
                        margin-top: 1rem;
                    }
                }

                &.last div {
                    border-left: none;
                    border-right: 3px dashed $primary-yellow;
                    left: auto;
                    right: -7%;
                    text-align: right;
                }

                &.odd div {
                    top: -16px;
                    transform: translateY(-100%);
                }

                &.even div {
                    margin-top: 45px;
                    padding: 15px 15px 0 15px;

                    p {
                        margin-bottom: 0
                    }

                    ::before {
                        top: -16px;
                        border-width: 8px 0 0 8px;
                        border-color: transparent transparent transparent white;
                    }
                }
            }
        }

    }
}

.players {

    h2 span {
        background-color: $primary-yellow;
        color: $dark-purple
    }

    .big-players {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/IndiaElects_MainCandidates.png);
        background-repeat: no-repeat;
        background-size: auto 100%;
        height: 592px;
    }

    .bump-down {
        margin-top: 100px;
    }

    .other-parties {
        margin-bottom: 100px;

        p {
            margin-bottom: 0;
        }

        p:first-of-type {
            color: $primary-yellow;
            font-family: $roman-normal-bold;
        }

        .gutters {
            padding: 150px 15px 0 15px;
        }

        .kazhagam {
            background-image: url(https://ceipimg.s3.amazonaws.com/static/IndiaElects_M.K.Stalin.png);
            background-repeat: no-repeat;
            background-size: contain;
            height: 150px;
        }

        .banerjee {
            background-image: url(https://ceipimg.s3.amazonaws.com/static/IndiaElects_MamataBanerjee.png);
            background-repeat: no-repeat;
            background-size: contain;
            height: 150px;
        }

        .kejriwal {
            background-image: url(https://ceipimg.s3.amazonaws.com/static/IndiaElects_ArvindKejriwal.png);
            background-repeat: no-repeat;
            background-size: contain;
            height: 150px;
        }
    }
}

.map {
    h2 span {
        background-color: $dark-purple;
    }

    .col-30 .gutters {
        margin-left: calc($gutter * 2.5)
    }

    .disadvantaged {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/IndiaElects_CasteMap.png);
        background-repeat: no-repeat;
        background-size: contain;
        height: 700px;
        background-position: top right;
    }

    .voter {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/GettyImages-1238343112.jpg);
        background-repeat: no-repeat;
        background-size: 100% auto;
        height: 900px;
    }
}

.battleground {
    h2 span {
        background-color: $dark-purple;
    }

    .col-30 .gutters {
        margin-left: calc($gutter * 2.5)
    }

    .big-map {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/IndiaElects_StatesMap.png);
        background-repeat: no-repeat;
        background-size: contain;
        height: 700px;
        background-position: top right;
    }

    .accordion-trigger {
        border-bottom: 1px solid $black;
        border-radius: 0;
        background: transparent;
        color: $teal;
        font-size: 1.5em;
        padding-left: 0;
        padding-right: 0;
        padding-bottom: 1rem;
        margin-bottom: 1rem;

        span {
            color: $black;
            font-size: .7em;
            padding-left: 15px
        }

        &:after {
            content: "";
            display: inline-block;
            border-top: 10px solid;
            border-left: 7px solid transparent;
            border-right: 7px solid transparent;
            margin: 0 0 0 5px;
        }
    }

    .uttar-pradesh {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/IndiaElects_UttarPradesh.png);
        background-repeat: no-repeat;
        background-size: contain;
        height: 300px;
        background-position: top right;
    }

    .maharashtra {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/IndiaElects_Maharashtra.png);
        background-repeat: no-repeat;
        background-size: contain;
        height: 300px;
        background-position: top right;
    }

    .karnataka {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/IndiaElects_Karnataka.png);
        background-repeat: no-repeat;
        background-size: contain;
        height: 300px;
        background-position: top right;
    }


}

.results {
    background-color: $dark-purple;
    color: $white;

    h2 span {
        background-color: $primary-yellow;
        color: $dark-purple
    }

    .results-2019 {
        background-image: url(https://ceipimg.s3.amazonaws.com/static/IndiaElects_SeatingChart.png);
        background-repeat: no-repeat;
        background-size: contain;
        height: 350px;
        background-position: top right;
    }
}

.conclusion {
    border-top: none;

    .doc-related {
        display: inline-block;
        margin: 0 0 calc($gutter * 2) 0;
        width: 970px;

        h3 {
            border-bottom: 2px solid $gold-medium;
            color: $gold-medium;
            display: inline-block;
            font-family: $roman-normal-bold;
            font-weight: normal;
            font-size: 2rem;
            line-height: 1.1em;
            margin-bottom: $gutter;
            padding-bottom: calc($gutter / 2);
            text-transform: uppercase;
            width: 100%;
        }

        .doc-related__item {
            background-color: transparent;
            float: left;
            margin-right: 30px;
            width: 220px;

            &:last-child {
                margin-right: 0;
            }

            a {
                background-position: top center;
                background-repeat: no-repeat;
                background-size: 220px auto;
                color: $black;
                display: inline-block;
                font-family: $roman-normal-bold;
                font-size: 1.6rem;
                padding-top: 125px;
                width: 100%;

                .title {
                    line-height: 1.6em;
                    margin-bottom: calc($gutter / 4);

                    &:hover {
                        text-decoration: underline;
                    }
                }

                .small-text {
                    color: $gray-dark;
                    font-family: $roman-normal-book;
                    font-size: 1rem;
                    line-height: 1.1em;

                    text-transform: uppercase;

                    em {
                        font-family: $roman-italic-book;
                        font-variant: none;
                    }
                }
            }
        }
    }
}



@include media-query(tablet) {

    .intro .intro-graphics {
        height: 300px;
    }

    .lok-sabha {
        .col-50 {
            width: 100%;
        }

        .lok-sabha-building {
            background-position: center;
            background-size: auto 100%;
            height: 190px;
            margin-top: $gutter;
        }
    }

    .logistics {

        .logistics-bridge,
        .logistics-elephants {
            height: 550px;
        }
    }

    .timeline {

        .timeline-section ol li div.text p {
            font-size: 0.75em;
            line-height: 1.3em;
        }
    }

    .players {
        .shift-right {
            margin-left: $gutter;
        }

        .big-players {
            height: 452px;
        }

        .full-width {
            margin-left: $gutter;
        }
    }

    .map {
        .voter {
            height: 500px;
        }
    }


    .results {
        .col-50 {
            width: 100%;
        }

        .results-seats {
            background-position: center;
            margin-left: 0;
            margin-top: $gutter;
        }
    }

    .conclusion {
        .doc-related {
            padding-left: 2vw;
            padding-right: 2vw;
            margin-left: 0;
            margin-right: 0;
            width: 92.5vw;

            .doc-related__item {
                margin-right: 2.8vw;
                margin-top: $gutter;
                width: 41vw;

                a {
                    background-size: auto 16vh;
                    padding-top: 17vh;
                }
            }
        }
    }
}

@include media-query(tablet-lg) {
    .logistics .logistics-bridge {
        height: 750px;
    }
}

@include media-query(phone) {

    .col {
        width: 100%;
    }

    .intro {
        padding-top: 0;

        .intro-graphics {
            background-position: center;
            margin-top: $gutter;
        }
    }

    .lok-sabha {
        background-position: right top;

        .gov,
        .money,
        .negotiations {
            background-position: center top;
            height: 80px;
        }

        ul {
            padding-bottom: 160px;
        }

        li .gutters {
            margin-top: calc($gutter * 4);
            padding-top: calc($gutter * 2.5);
            margin-left: 0;
        }

        li.gov .gutters {
            margin-top: 0;
        }
    }

    .logistics {
        .logistics-graphic {
            background-position: center;
            height: 300px;
        }

        .logistics-bridge {
            height: 550px;
        }

        .caption {
            margin-bottom: 15px;
        }
    }

    .timeline {

        .timeline-section {
            margin-top: $gutter;

            ol {
                padding-top: $gutter;

                li {
                    display: block;
                    height: calc($gutter * 6);
                    margin-bottom: 0;
                    width: 5%;

                    &:first-child {
                        margin-left: 0;
                    }

                    &.odd div {
                        top: 0;
                        padding-top: 1em;
                        transform: translateY(50%);
                    }

                    div {
                        border-left: none;
                        border-top: 3px dashed $primary-yellow;
                        bottom: 0;
                        padding-bottom: 0;
                        transform: translateY(50%);
                        width: 80vw;
                    }

                    &.last div {
                        border-right: none;
                        left: calc(100% + 7px);
                        right: auto;
                        text-align: left;
                    }

                    &.even div {
                        margin-top: 0;
                        padding-top: 1em;
                        top: 0;
                    }

                    &.entry:not(:last-child)::after {
                        top: 50%;
                        left: -webkit-calc(100% - 15px);
                        left: calc(100% - 15px);
                    }
                }
            }
        }
    }

    .players {
        .big-players {
            height: 390px;
        }

        .other-parties div {
            margin-bottom: 150px;
        }

    }

    .map {
        .disadvantaged {
            height: 400px;
        }

        .voter {
            height: 450px;
        }
    }

    .battleground .big-map {
        height: 400px;
    }


    .conclusion {
        .doc-related {
            background-color: transparent;
            padding: 0;
            width: 100%;

            .doc-related__item {
                min-height: auto;
                width: 100%;
                margin: 0 0 $gutter 0;

                &:nth-child(odd) {
                    margin-right: 0;
                }

                &:nth-child(even) {
                    margin-left: 0;
                }

                a {
                    background-size: auto 28vh;
                    padding-top: 29vh;
                }
            }
        }
    }
}
</style>